import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useTranslation } from 'react-i18next';

import { Answer } from '@/components/common/Answer';
import type { AnswerOut, TrendPoint } from '@/services/types';

/** One share drawn as a line: which code, how it is labelled, how it is stroked. */
export interface TrendSeries {
  /** The key in `TrendPoint.shares`. */
  code: string;
  /** Namespaced locale key for the legend label. */
  labelKey: string;
  stroke: string;
  dashed?: boolean;
}

/** Today's ANATEL series, and the defaults: fine solid, warning dashed. */
const ANATEL_SERIES: TrendSeries[] = [
  { code: 'FINE', labelKey: 'anatel:overview.trend.fineShare', stroke: 'hsl(var(--primary))' },
  {
    code: 'WARNING',
    labelKey: 'anatel:overview.trend.warningShare',
    stroke: 'hsl(var(--muted-foreground))',
    dashed: true,
  },
];

/**
 * Shares as lines, with the annual DENOMINATOR drawn behind them as bars.
 *
 * The bars are the whole point. Volume runs from 43 decisions in 2015 to 3,354
 * in 2018 and back to 733 in 2025, so the fine share in two different years is
 * computed over populations differing several-fold. Plotting the rates alone
 * invites precisely the year-over-year comparison that is not valid, and the
 * reader has no way to know. This is the same failure the sibling platform hit
 * twice: a rate over two different populations is not a comparison.
 *
 * Generalised on 2026-09-14 so the CADE overview draws its own codes through
 * the same chart: the title, the help, the volume label and the series are
 * props, and every default is today's ANATEL value, so the ANATEL caller is
 * unchanged. The bars-behind-lines rule is not a prop.
 */
export function OutcomeTrendPanel({
  answer,
  titleKey = 'anatel:overview.trend.title',
  helpKey = 'anatel:overview.trend.help',
  volumeLabelKey = 'anatel:overview.trend.volume',
  series = ANATEL_SERIES,
}: {
  answer: AnswerOut<TrendPoint[]>;
  titleKey?: string;
  helpKey?: string;
  volumeLabelKey?: string;
  series?: TrendSeries[];
}) {
  const { t, i18n } = useTranslation(['anatel', 'cade', 'common']);
  const percent = new Intl.NumberFormat(i18n.language, {
    style: 'percent',
    maximumFractionDigits: 1,
  });
  const number = new Intl.NumberFormat(i18n.language);

  return (
    <section className="rounded-lg border bg-card p-4">
      <h2 className="mb-3 text-sm font-semibold text-foreground">{t(titleKey)}</h2>
      <Answer answer={answer} help={t(helpKey)}>
        {(points) => {
          const data = points.map((point) => ({
            period: point.period,
            total: point.total,
            ...Object.fromEntries(series.map((line) => [line.code, point.shares[line.code] ?? 0])),
          }));
          return (
            <div className="h-72 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={data} margin={{ top: 8, right: 8, bottom: 8, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                  <XAxis dataKey="period" tick={{ fontSize: 11 }} className="fill-muted-foreground" />
                  <YAxis
                    yAxisId="share"
                    tickFormatter={(v: number) => percent.format(v)}
                    domain={[0, 1]}
                    tick={{ fontSize: 11 }}
                    width={48}
                    className="fill-muted-foreground"
                  />
                  <YAxis
                    yAxisId="volume"
                    orientation="right"
                    tickFormatter={(v: number) => number.format(v)}
                    tick={{ fontSize: 11 }}
                    width={48}
                    className="fill-muted-foreground"
                  />
                  <Tooltip
                    formatter={(value: number, name: string) =>
                      name === t(volumeLabelKey) ? number.format(value) : percent.format(value)
                    }
                    contentStyle={{
                      background: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '0.25rem',
                      fontSize: '0.75rem',
                    }}
                  />
                  <Legend wrapperStyle={{ fontSize: '0.75rem' }} />
                  <Bar
                    yAxisId="volume"
                    dataKey="total"
                    name={t(volumeLabelKey)}
                    fill="hsl(var(--muted-foreground))"
                    fillOpacity={0.25}
                    radius={[2, 2, 0, 0]}
                  />
                  {series.map((line) => (
                    <Line
                      key={line.code}
                      yAxisId="share"
                      type="monotone"
                      dataKey={line.code}
                      name={t(line.labelKey)}
                      stroke={line.stroke}
                      strokeWidth={2}
                      strokeDasharray={line.dashed ? '4 3' : undefined}
                      dot={false}
                    />
                  ))}
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          );
        }}
      </Answer>
    </section>
  );
}
